import { BIWEEKLY_DAY_RANGES, FOURTEEN_WEEKS, MONTH_WEEKS, MONTHLY_DAY_RANGES, PERIODICITY_CONTROLS_BY_TYPE, WEEK_DAYS } from './../../shared/constants/periodicities';
import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { legalEntityReference } from 'src/app/models/legal-entity.model';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize, switchMap, catchError, map, Observable, of, Subject, throwError, timer } from 'rxjs';
import { Periodicity, PeriodicityCategoriesResponse } from 'src/app/models/periodicity-categories-response.interface';
import { PeriodicityType, PeriodicityTypesResponse } from 'src/app/models/periodicity-types-response.interface';
import { DiscountType } from 'src/app/models/discount-type.model';
import { Settlement, SettlementFiscalAttributes, SettlementFiscalPayload } from 'src/app/models/settlement.model';
import { SettlementsService } from 'src/app/services/settlements.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CatalogsService } from 'src/app/shared/services/catalogs.service';
import { IconsService } from 'src/app/shared/services/icons.service';
import {
  buildDiscountTypeReference,
  DEFAULT_DISCOUNT_TYPE_ID,
  DEFAULT_DISCOUNT_TYPES,
  getDiscountTypeOptionLabel,
  resolveDiscountTypeId
} from 'src/app/shared/utils/discount-type.utils';
import { resetControl, setControlsValidators } from 'src/app/shared/utils/utils';
import { formatContractDate, shouldWarnContractExpiration } from 'src/app/shared/utils/contract-term.utils';
import { RFCValidator } from 'src/app/shared/validators/rfc';
import { environment } from 'src/environments/environment';
import { BreadcrumbService } from 'xng-breadcrumb';

@Component({
  selector: 'app-form-settlement',
  template: `
    <form [formGroup]="settlementForm" (ngSubmit)="updateSettlement()">
      <p *ngIf="isFormLocked" class="text-muted mt-3" role="status">No tienes permiso para editar la configuración de este cliente.</p>
      <fieldset [disabled]="isFormLocked || loading">
      <div class="row mt-3">
        <div class="col-md-11 ms-2">
          <h4>Editar {{ formTitle }} <fa-icon [icon]="icons.faCircleNotch" animation="spin" *ngIf="loading" class="ms-2 text-info"></fa-icon></h4>
          <p>Configura la información del {{ formTitle }}. Estos datos se mostrarán en todas las aplicaciones. Para más información visita nuestar guía de uso.</p>
        </div>

        <div class="form-group mb-4 mt-3" *ngIf="canEditPublished && !isFormLocked">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="statusToggle" formControlName="published">
            <label class="form-check-label" for="statusToggle">
              {{ settlementForm.get('published')?.value ? 'Publicado' : 'Borrador' }}
            </label>
          </div>
          <small class="form-text text-muted">
            Los {{formTitle}}s publicados solo pueden ser editados por administradores con permisos especiales.
          </small>
        </div>
        <!-- Display readonly badge for users without permission -->
        <div class="mb-4 mt-3" *ngIf="isPublishedAndLocked">
          <div class="badge bg-success">Publicado</div>
          <small class="form-text text-muted d-block mt-1">
            Este {{formTitle}} está publicado. Contacte a un administrador para realizar cambios.
          </small>
        </div>
        
        <div class="form-group required mb-4 mt-3">
          <label for="name" class="form-label ms-2">Nombre del {{formTitle}}</label>
          <input type="text" class="form-control" id="name" placeholder="Nombre" formControlName="name">
          <div *ngIf="sfc['name'].touched && sfc['name'].invalid">
            <div *ngIf="sfc['name'].errors?.['required']" class="text-danger text-sm">El nombre del {{formTitle}} es requerido.</div>
          </div>
        </div>

        <div class="form-group required mb-4">
          <label for="description" class="form-label">Acuerdos específicos del cliente</label>
          <div class="form-check">
            <input id="description_na" class="form-check-input" type="checkbox" [(ngModel)]="descriptionNA" [ngModelOptions]="{standalone: true}">
            <label class="form-check-label" for="description_na">No Aplica</label>
          </div>
          <textarea class="form-control" id="description" formControlName="description" rows="3" [readOnly]="descriptionNA"></textarea>
          <div *ngIf="sfc['description'].touched && sfc['description'].invalid">
            <div *ngIf="sfc['description'].errors?.['required']" class="text-danger text-sm">Los acuerdos del {{formTitle}} son requeridos.</div>
          </div>
        </div>

        <div class="col-md-11 mt-3" *ngIf="!isPublishedAndLocked">
          <div class="rounded-3 p-4 border border-1 clearfix" [ngStyle]="{ 'background-color': color }">
            <div class="border rounded-4 float-start me-3" style="width: 84px; height: 84px; overflow: hidden;">
              <img [src]="settlement?.image" width="100%">
            </div>
            <h3 class="text-dark mt-2">{{sfc['name'].value}}</h3>
            <input type="file" id="logo" (change)="loadImage($event)" > <!-- class="inputfile" -->
            <!-- label for="file"><strong>Cargar imagen</strong></!-->
          </div>
          <p class="mt-2 text-muted">Nota: Este archivo debe ser en .PNG y las dimensiones mínimas de la imagen es de 514 x 514 y máximas de 2080 x 2080.</p>
        </div>
        <!-- div class="col-md-1 mt-3">
          <input [value]="color" [style.background]="color" class="rounded-2 border-1 border-secondary shadow" [ngStyle]="{ 'color': color }" (change)="updateColor($event)" style="width: 34px; height: 34px;"/>
        </div -->

        
        <div class="form-group mb-4 mt-3">
          <label for="settlement_admin_name" class="form-label">Nombre del administrador</label>
          <input id="settlement_admin_name" class="form-control" type="text" formControlName="admin_name">
        </div>
        <div class="form-group mb-4">
          <label for="settlement_email" class="form-label">Correo electrónico de contacto</label>
          <input id="settlement_email" class="form-control" type="email" formControlName="email">
          <div *ngIf="sfc['email'].touched && sfc['email'].errors?.['email']" class="text-danger text-sm">Introduce un correo electrónico válido.</div>
        </div>
        <div class="form-group mb-4">
          <label for="settlement_phone" class="form-label">Teléfono de contacto</label>
          <input id="settlement_phone" class="form-control" type="tel" formControlName="phone">
        </div>
        <h4 class="mt-4">Configuración del {{formTitle}}</h4>
        <hr>
        <div class="form-group mb-4">
          <app-legal-entity-select formControlName="legal_entity_id" [required]="true"
            [isCommercial]="isComercial" label="Razón social que emite factura" inputId="settlement-legal-entity"></app-legal-entity-select>
          <div *ngIf="sfc['legal_entity_id'].touched && sfc['legal_entity_id'].invalid" class="text-danger text-sm">
            Selecciona una razón social válida para este cliente.
          </div>
        </div>
        <div class="form-group required mb-4">
          <label for="factor" class="form-label ms-2">Factor</label>
          <input type="number" 
                class="form-control" 
                id="factor" 
                placeholder="" 
                formControlName="factor">
          <div *ngIf="sfc['factor'].touched && sfc['factor'].invalid">
            <div *ngIf="sfc['factor'].errors?.['required']" class="text-danger text-sm">
              El factor es requerido.
            </div>
            <div *ngIf="sfc['factor'].errors?.['min']" class="text-danger text-sm">
              El factor debe ser mayor a 0.1
            </div>
          </div>
        </div>
        <div class="form-check required mt-2">
          <input class="form-check-input" 
                type="checkbox" 
                id="hideFactor"
                formControlName="hide_factor">
          <label class="form-check-label" for="hideFactor">
            Ocultar factor en recibos
          </label>
        </div>
        <div class="form-group required mb-4">
          <label for="admin_price" class="form-label ms-2">Costo administrativo ($ IVA incluido)</label>
          <input type="number" min="0" class="form-control" id="admin_price" placeholder="" formControlName="admin_price">
          <div *ngIf="sfc['admin_price'].touched && sfc['admin_price'].invalid">
            <div *ngIf="sfc['admin_price'].errors?.['required']" class="text-danger text-sm">El costo administrativo es requerido.</div>
          </div>
        </div>
        <div class="form-group required mb-4">
          <label for="interests" class="form-label ms-2">Cargos moratorios (%)</label>
          <input type="number" min="0" class="form-control" id="interests" placeholder="" formControlName="interests">
          <div *ngIf="sfc['interests'].touched && sfc['interests'].invalid">
            <div *ngIf="sfc['interests'].errors?.['required']" class="text-danger text-sm">El factor es requerido.</div>
          </div>
        </div>
        <div class="form-group required mb-4">
          <label for="reconexion_fee" class="form-label ms-2">Costo de reconexión ($ IVA incluido)</label>
          <input type="number" min="0" class="form-control" id="reconexion_fee" placeholder="" formControlName="reconexion_fee">
          <div *ngIf="sfc['reconexion_fee'].touched && sfc['reconexion_fee'].invalid">
            <div *ngIf="sfc['reconexion_fee'].errors?.['required']" class="text-danger text-sm">El costo de reconexión es requerido.</div>
          </div>
        </div>
        <div class="form-group required mb-4">
          <label for="discount_type_id" class="form-label ms-2">Tipo de descuento</label>
          <select class="form-select" id="discount_type_id" formControlName="discount_type_id">
            <option *ngFor="let discountType of discountTypes" [ngValue]="discountType.id">
              {{ discountTypeOptionLabel(discountType) }}
            </option>
          </select>
          <div *ngIf="sfc['discount_type_id'].touched && sfc['discount_type_id'].invalid">
            <div *ngIf="sfc['discount_type_id'].errors?.['required']" class="text-danger text-sm">
              El tipo de descuento es requerido.
            </div>
          </div>
        </div>
        <div class="form-group required mb-4">
          <label for="discount" class="form-label ms-2">{{ discountInputLabel }}</label>
          <div class="input-group">
            <span *ngIf="discountSymbol === '$'" class="input-group-text">{{ discountSymbol }}</span>
            <input type="number" min="0" step="0.01" class="form-control" id="discount" placeholder="" formControlName="discount">
            <span *ngIf="discountSymbol !== '$'" class="input-group-text">{{ discountSymbol }}</span>
          </div>
          <div *ngIf="sfc['discount'].touched && sfc['discount'].invalid">
            <div *ngIf="sfc['discount'].errors?.['required']" class="text-danger text-sm">El descuento es requerido.</div>
            <div *ngIf="sfc['discount'].errors?.['min']" class="text-danger text-sm">El descuento no puede ser negativo.</div>
            <div *ngIf="sfc['discount'].errors?.['pattern']" class="text-danger text-sm">El descuento admite hasta 2 decimales.</div>
          </div>
        </div>
        <div class="form-check mb-4 ms-4">
          <input class="form-check-input" type="checkbox" id="show_discount_on_receipt" formControlName="show_discount_on_receipt">
          <label class="form-check-label" for="show_discount_on_receipt">Mostrar descuento en el recibo</label>
        </div>
        <div class="form-group required mb-4">
          <label for="invoice_limit" class="form-label ms-2">Días para solicitud de factura</label>
          <input type="number" min="0" class="form-control" id="invoice_limit" placeholder="" formControlName="invoice_limit">
          <div *ngIf="sfc['invoice_limit'].touched && sfc['invoice_limit'].invalid">
            <div *ngIf="sfc['invoice_limit'].errors?.['required']" class="text-danger text-sm">Los días límite para solicitar factura es requerido.</div>
          </div>
        </div>
        <div class="form-group required mb-4">
          <label for="credit" class="form-label ms-2">Días de crédito</label>
          <input type="number" min="0" class="form-control" id="credit" placeholder="" formControlName="credit">
          <div *ngIf="sfc['credit'].touched && sfc['credit'].invalid">
            <div *ngIf="sfc['credit'].errors?.['required']" class="text-danger text-sm">El crédito es requerido.</div>
          </div>
        </div>
        <div class="form-group required mb-4">
          <label for="merchant_id" class="form-label ms-2">Motor de pago</label>
          <select class="form-select form-select" id="merchant_id" placeholder="" formControlName="merchant_id">
            <option value="" disabled>Selecciona un motor de pago</option>
            <!-- option value="actnet">Actnet</option -->
            @if(isComercial) {
              <option value="stp">STP</option>
            }
            @else {
              <option value="openpay">Openpay</option>
            }
          </select>
          <div *ngIf="sfc['merchant_id'].touched && sfc['merchant_id'].invalid">
            <div *ngIf="sfc['merchant_id'].errors?.['required']" class="text-danger text-sm">El motor de pago es requerido.</div>
          </div>
        </div>
        <div class="form-group required mb-4">
          <label for="periodicity" class="form-label ms-2">Periodicidad de ciclo</label>
          <select id="periodicity" class="form-select form-select" formControlName="periodicity" (change)="periodicityChange($event)">
            <option value="" disabled>Selecciona una periodicidad</option>
            <option *ngFor="let period of periodicities" [value]="period.id">{{period.description}}</option>
          </select>
          <div *ngIf="sfc['periodicity'].touched && sfc['periodicity'].invalid">
            <div *ngIf="sfc['periodicity'].errors?.['required']" class="text-danger text-sm">La periodicidad es requerido.</div>
          </div>
        </div>

        <div formGroupName="periodicity_configuration_attributes">
          <ng-container *ngIf="periodicityTypes.length > 0">
            <div class="form-group required mb-4">
              <label for="tipo_periodicidad" class="form-label ms-2">Tipo de periodicidad</label>
            <select id="tipo_periodicidad" class="form-select form-select" formControlName="periodicity_type_id" (change)="periodicityTypeChange($event)">
              <option value="" disabled>Selecciona una periodicidad</option>
              <option *ngFor="let type of periodicityTypes" [ngValue]="type.id">{{type.description || type.name}}</option>
            </select>
            <div *ngIf="pcgc['periodicity_type_id'].touched && pcgc['periodicity_type_id'].invalid">
              <div *ngIf="pcgc['periodicity_type_id'].errors?.['required']" class="text-danger text-sm">
                El tipo de periodicidad es requerido.
              </div>
            </div>
            </div>
          </ng-container>
          
          <ng-container *ngIf="getPeriodicityConfigGroup.get('periodicity_type_id')?.value === 1">
            <div class="form-group required mb-4">
              <label for="dia_del_mes" class="form-label ms-2">Día del mes</label>
              <input [min]="monthly_day_ranges.min" [max]="monthly_day_ranges.max" type="number" class="form-control" id="dia_del_mes" formControlName="day">
              <div *ngIf="pcgc['day'].touched && pcgc['day'].invalid">
              <div *ngIf="pcgc['day'].errors?.['required']" class="text-danger text-sm">
                El día del mes es requerido.
              </div>
            </div>
            </div>
          </ng-container>

          <ng-container *ngIf="[2, 5].includes(getPeriodicityConfigGroup.get('periodicity_type_id')?.value) ">
            <div class="form-group required mb-4">
              <label for="numero_semana" class="form-label ms-2">Número de semana</label>
              <select id="numero_semana" class="form-select form-select" formControlName="week_number">
                <option value="" disabled>Selecciona una semana</option>
                <option *ngFor="let week of list_weeks" [ngValue]="week">{{ week }}</option>
              </select>
              <div *ngIf="pcgc['week_number'].touched && pcgc['week_number'].invalid">
              <div *ngIf="pcgc['week_number'].errors?.['required']" class="text-danger text-sm">
                El número de semana es requerido.
              </div>
            </div>
            </div>
          </ng-container>

          <ng-container *ngIf="getPeriodicityConfigGroup.get('week_number')?.value || getPeriodicityConfigGroup.get('periodicity_type_id')?.value === 6">
            <div class="form-group required mb-4">
              <label for="dia_semana" class="form-label ms-2">Día de la semana</label>
              <select id="dia_semana" class="form-select form-select" formControlName="weekday">
                <option value="" disabled>Selecciona un día</option>
                <option *ngFor="let item of week_days" [ngValue]="item.day">{{ item.name }}</option>
              </select>
              <div *ngIf="pcgc['weekday'].touched && pcgc['weekday'].invalid">
              <div *ngIf="pcgc['weekday'].errors?.['required']" class="text-danger text-sm">
                El día de la semana es requerido.
              </div>
            </div>
            </div>
          </ng-container>

          <ng-container *ngIf="getPeriodicityConfigGroup.get('periodicity_type_id')?.value === 3">
            <div class="form-group required mb-4">
              <label for="dias_antes_fin_mes" class="form-label ms-2">Días antes del fin de mes</label>
              <input [min]="monthly_day_ranges.min" [max]="monthly_day_ranges.max" type="number" class="form-control" id="dias_antes_fin_mes" formControlName="day">
              <div *ngIf="pcgc['day'].touched && pcgc['day'].invalid">
              <div *ngIf="pcgc['day'].errors?.['required']" class="text-danger text-sm">
                Los días son requeridos.
              </div>
            </div>
            </div>
          </ng-container>

          <ng-container *ngIf="getPeriodicityConfigGroup.get('periodicity_type_id')?.value === 4">
            <div class="form-group required mb-4">
              <label for="días_antes_fin_quincena" class="form-label ms-2">Días antes del fin de quincena</label>
              <input [min]="biweekly_day_ranges.min" [max]="biweekly_day_ranges.max" type="number" class="form-control" id="días_antes_fin_quincena" formControlName="day">
              <div *ngIf="pcgc['day'].touched && pcgc['day'].invalid">
              <div *ngIf="pcgc['day'].errors?.['required']" class="text-danger text-sm">
                Los días son requeridos.
              </div>
              <div *ngIf="pcgc['day'].errors?.['min'] || pcgc['day'].errors?.['max'] || pcgc['day'].errors?.['pattern']" class="text-danger text-sm">
                Ingresa un número entero entre {{biweekly_day_ranges.min}} y {{biweekly_day_ranges.max}}.
              </div>
            </div>
            </div>
          </ng-container>
          
        </div>

        <!-- <div class="form-group required mb-4" *ngIf="sfc['periodicity'].value == 3">
          <label for="cycle_day" class="form-label ms-2">Inicio de ciclo (Día del mes)</label>
          <input type="number" class="form-control" id="cycle_day" placeholder="" formControlName="cycle_day">
          <div *ngIf="sfc['cycle_day'].touched && sfc['cycle_day'].invalid">
            <div *ngIf="sfc['cycle_day'].errors?.['required']" class="text-danger text-sm">El día es requerido.</div>
          </div>
        </div>

        <div class="form-group required mb-4" *ngIf="sfc['periodicity'].value != 3">
          <label for="cycle_day" class="form-label ms-2">Inicio de ciclo (Día de la semana)</label>
          <select class="form-select form-select" formControlName="cycle_day">
            <option value="" disabled>Selecciona una periodicidad</option>
            <option *ngFor="let day of weekDays" [value]="day.id">{{day.name}}</option>
          </select>
          <div *ngIf="sfc['cycle_day'].touched && sfc['cycle_day'].invalid">
            <div *ngIf="sfc['cycle_day'].errors?.['required']" class="text-danger text-sm">La perodicidad es requerido.</div>
          </div>
        </div> -->
        
        <div class="form-group required mb-4">
          <label for="corte" class="form-label ms-2">Corte a partir de periodos de adeudos</label>
          <input type="number" class="form-control" id="corte" placeholder="" formControlName="corte">
          <div *ngIf="sfc['corte'].touched && sfc['corte'].invalid">
            <div *ngIf="sfc['corte'].errors?.['required']" class="text-danger text-sm">El periodo de corte es requerido.</div>
          </div>
        </div>
        <div class="form-group mb-4">
          <label for="zone_id" class="form-label ms-2">Zona de distribución</label>
          <select class="form-select form-select" formControlName="zone_id">
            <option value="" disabled>Selecciona una zona</option>
            <option *ngFor="let zone of availZones" [value]="zone.id">{{zone.name}} {{zone.description}}</option>
          </select>
          <div *ngIf="sfc['zone_id'].touched && sfc['zone_id'].invalid">
            <div *ngIf="sfc['zone_id'].errors?.['required']" class="text-danger text-sm">La zona de distribución es requerida.</div>
          </div>
        </div>
        <!-- section class="mt-3">
          <section app-contract-unit
            [settlement]="settlement"
          ></section>
        </!-->
        <h5 class="mt-4">Vigencia del contrato</h5>
        <div class="row">
          <div class="col-md-6">
            <div class="form-group required mb-4">
              <label for="contract_term_years" class="form-label ms-2">Temporalidad del contrato (años)</label>
              <input
                type="number"
                min="1"
                step="1"
                class="form-control"
                id="contract_term_years"
                formControlName="contract_term_years">
              <div *ngIf="sfc['contract_term_years'].touched && sfc['contract_term_years'].invalid">
                <div *ngIf="sfc['contract_term_years'].errors?.['required']" class="text-danger text-sm">
                  La temporalidad del contrato es requerida.
                </div>
                <div *ngIf="sfc['contract_term_years'].errors?.['min'] || sfc['contract_term_years'].errors?.['pattern']" class="text-danger text-sm">
                  La temporalidad debe ser un número entero mayor a cero.
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-check form-switch mt-md-4 pt-md-2 mb-4">
              <input
                class="form-check-input"
                type="checkbox"
                id="automatic_renewal"
                formControlName="automatic_renewal">
              <label class="form-check-label" for="automatic_renewal">Renovación automática</label>
              <small
                *ngIf="sfc['automatic_renewal'].value === null || sfc['automatic_renewal'].value === undefined"
                class="form-text text-muted d-block">
                Sin configuración para este contrato.
              </small>
            </div>
          </div>
        </div>
        <div class="form-group mb-4" *ngIf="sfc['contract_end_date'].value">
          <label for="contract_end_date" class="form-label ms-2">Fecha de terminación</label>
          <input
            type="text"
            class="form-control"
            id="contract_end_date"
            [value]="formatContractDate(sfc['contract_end_date'].value)"
            disabled>
          <small class="form-text text-muted">Esta fecha es calculada por el sistema.</small>
        </div>
        <div class="alert alert-warning" role="alert" *ngIf="contractExpirationWarning">
          El contrato no tiene renovación automática y vence dentro de los próximos tres meses o ya venció.
        </div>
        <app-contract-history *ngIf="settlement?.id && !isFormLocked" [settlementId]="settlement!.id!" [editable]="true"></app-contract-history>
        <div class="form-group mb-4" *ngIf="!settlement?.id && !sfc['contrato'].value">
          <label for="contract_file" class="form-label ms-2">Contrato del {{formTitle}}</label>
          <input id="contract_file" type="file" accept="application/pdf,.pdf" class="form-control" (change)="loadPDF($event)" >
        </div>
        
        <div class="form-group mb-4" *ngIf="!settlement?.id && sfc['contrato'].value">
          <label for="name" class="form-label ms-2">Contrato del {{formTitle}}</label><br>
          <button type="button" class="btn btn-link ms-2" (click)="downloadContract()">
            <fa-icon [icon]="icons.faFilePdf" class="text-primary me-2"></fa-icon>
            Descargar contrato
          </button>
          <button type="button" class="btn btn-link ms-2" (click)="removeContrato()">
            <fa-icon [icon]="icons.faTrashAlt" class="text-danger ms-2"></fa-icon>
          </button>
        </div>
        <h4 class="mt-4">Datos fiscales de la administración del {{formTitle}}</h4>
        <p class="text-muted">
          Captura la información fiscal que se utilizará para la administración del {{formTitle}}.
        </p>
        <hr>
        <div formGroupName="settlement_fiscal_attributes" class="row">
          <div class="col-md-6">
            <div class="form-group mb-4">
              <label for="settlement_fiscal_rfc" class="form-label ms-2">RFC</label>
              <input
                type="text"
                appRfcValidator
                [isComercial]="isComercial"
                [allowBoth]="true"
                maxlength="13"
                class="form-control"
                id="settlement_fiscal_rfc"
                formControlName="rfc">
              <div *ngIf="sfac['rfc'].touched && sfac['rfc'].invalid">
                <div *ngIf="sfac['rfc'].errors?.['rfcInvalid']" class="text-danger text-sm">
                  El RFC es inválido.
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-4">
              <label for="settlement_fiscal_razon_social" class="form-label ms-2">Razón social</label>
              <input
                type="text"
                class="form-control"
                id="settlement_fiscal_razon_social"
                formControlName="razon_social">
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-4">
              <label for="settlement_fiscal_zip_code" class="form-label ms-2">Código postal fiscal</label>
              <input
                type="text"
                inputmode="numeric"
                maxlength="5"
                class="form-control"
                id="settlement_fiscal_zip_code"
                formControlName="zip_code">
              <div *ngIf="sfac['zip_code'].touched && sfac['zip_code'].invalid">
                <div *ngIf="sfac['zip_code'].errors?.['pattern']" class="text-danger text-sm">
                  El código postal fiscal debe contener 5 dígitos.
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-4">
              <label for="settlement_fiscal_regimen" class="form-label ms-2">Régimen fiscal</label>
              <select
                class="form-select"
                id="settlement_fiscal_regimen"
                formControlName="regimen_fiscal">
                <option value="">Selecciona un régimen fiscal</option>
                <option *ngFor="let regimen of catalogs.regimenes" [value]="regimen.id">
                  {{regimen.id}} - {{regimen.name}}
                </option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-4">
              <label for="settlement_fiscal_uso_cfdi" class="form-label ms-2">Uso CFDI</label>
              <select
                class="form-select"
                id="settlement_fiscal_uso_cfdi"
                formControlName="uso_cfdi">
                <option value="">Selecciona un uso de CFDI</option>
                <option *ngFor="let usoCfdi of catalogs.usosCFDI" [value]="usoCfdi.id">
                  {{usoCfdi.id}} - {{usoCfdi.name}}
                </option>
              </select>
            </div>
          </div>
        </div>
        <h4 class="mt-4">Dirección del {{formTitle}}</h4>
        <hr>
        <div class="form-group required mt-4 mb-4">
          <label for="zip_code" class="form-label ms-2">Código postal</label>
          <div class="position-relative">
            <input type="text" 
              class="form-control" 
              id="zip_code"
              formControlName="zip"
              placeholder="Código postal del {{formTitle}}"
              (input)="searchPostalCode($event)"
              (focus)="cpResults = []">
            <div class="position-absolute end-0 top-50 translate-middle-y pe-2" *ngIf="searchingZip">
              <fa-icon [icon]="icons.faCircleNotch" animation="spin" class="text-secondary"></fa-icon>
            </div>
          </div>

          <div *ngIf="cpResults.length > 0" class="position-relative">
            <div class="list-group position-absolute w-100 shadow-sm">
              <a class="list-group-item list-group-item-action" 
                *ngFor="let result of cpResults" 
                (click)="selectCP(result)">
                {{result.settlement}} - {{result.municipality}} {{result.city}} {{result.country}}
              </a>
            </div>
          </div>
          <div *ngIf="sfc['zip']?.touched && sfc['zip']?.invalid">
            <div *ngIf="sfc['zip'].errors?.['required']" class="text-danger text-sm">El Código Postal es requerido.</div>
          </div>
        </div>
        <div class="form-group required mb-4">
          <label for="name" class="form-label ms-2">Calle</label>
          <input type="text" class="form-control" id="name" placeholder="Dirección del {{formTitle}}" formControlName="address">
          <div *ngIf="sfc['address'].touched && sfc['address'].invalid">
            <div *ngIf="sfc['address'].errors?.['required']" class="text-danger text-sm">La calle es requerida.</div>
          </div>
        </div>
        <div class="form-group mb-4">
          <label for="name" class="form-label ms-2">Número exterior</label>
          <input type="text" class="form-control" id="name" placeholder="Número exterior" formControlName="outer_number">
        </div>
        <div class="form-group required mb-4">
          <label for="country" class="form-label ms-2">País</label>
          <input type="text" class="form-control" id="country" placeholder="País del {{formTitle}}" readonly formControlName="country">
          <div *ngIf="sfc['country'].touched && sfc['country'].invalid">
            <div *ngIf="sfc['country'].errors?.['required']" class="text-danger text-sm">El país es requerido.</div>
          </div>
        </div>
        <div class="form-group required mb-4">
          <label for="state" class="form-label ms-2">Estado</label>
          <input type="text" class="form-control" id="state" placeholder="Estado del {{formTitle}}" readonly formControlName="state">
          <div *ngIf="sfc['state'].touched && sfc['state'].invalid">
            <div *ngIf="sfc['state'].errors?.['required']" class="text-danger text-sm">El estado es requerido.</div>
          </div>
        </div>

        <div class="form-group required mb-4">
          <label for="city" class="form-label ms-2">Ciudad</label>
          <input type="text" class="form-control" id="city" placeholder="Ciudad del {{formTitle}}" readonly formControlName="city">
          <div *ngIf="sfc['city'].touched && sfc['city'].invalid">
            <div *ngIf="sfc['city'].errors?.['required']" class="text-danger text-sm">La ciudad es requerida.</div>
          </div>
        </div>

        <div class="form-group required mb-4">
          <label for="settlement" class="form-label ms-2">Asentamiento</label>
          <input type="text" class="form-control" id="settlement" placeholder="Asentamiento del {{formTitle}}" readonly formControlName="suburb">
          <div *ngIf="sfc['suburb'].touched && sfc['suburb'].invalid">
            <div *ngIf="sfc['suburb'].errors?.['required']" class="text-danger text-sm">El asentamiento es requerido.</div>
          </div>
        </div>

        <div class="form-group required mb-4">
          <label for="settlementType" class="form-label ms-2">Tipo de Asentamiento</label>
          <input type="text" class="form-control" id="settlementType" placeholder="Tipo de Asentamiento del {{formTitle}}" readonly formControlName="suburb_type">
          <div *ngIf="sfc['suburb_type'].touched && sfc['suburb_type'].invalid">
            <div *ngIf="sfc['suburb_type'].errors?.['required']" class="text-danger text-sm">El tipo de asentamiento es requerido.</div>
          </div>
        </div>

        <div class="form-group required mb-4">
          <label for="municipality" class="form-label ms-2">Delegación</label>
          <input type="text" class="form-control" id="municipality" placeholder="Asentamiento del {{formTitle}}" readonly formControlName="municipality">
          <div *ngIf="sfc['municipality'].touched && sfc['municipality'].invalid">
            <div *ngIf="sfc['municipality'].errors?.['required']" class="text-danger text-sm">La delegación es requerida.</div>
          </div>
        </div>

        <div class="form-group mb-4">
          <label for="latitude" class="form-label ms-2">Latitud</label>
          <input type="number" class="form-control" id="latitude" placeholder="Latitud del {{formTitle}}" formControlName="latitude">
        </div>
        <div class="form-group mb-4">
          <label for="longitude" class="form-label ms-2">Longitud</label>
          <input type="number" maxlength="6" class="form-control" id="longitude" placeholder="Longitud del {{formTitle}}" formControlName="longitude">
        </div>
        <div class="col-12 mt-4">
          <div class="form-group text-end">
            <button type="submit" class="btn btn-outline-zazz-dark me-2">Actualizar</button>
            <button type="button" class="btn btn-zazz-dark" *ngIf="settlement&&!settlement?.published" (click)="publishSettlement()">Publicar</button>
          </div>
        </div>
      </div>
      </fieldset>
      <div class="text-end mt-2"><button type="button" class="btn btn-link" (click)="cancel()">{{isFormLocked ? 'Volver' : 'Cancelar'}}</button></div>
    </form>
  `,
  styles: ``
})
export class FormSettlementComponent {
  private readonly destroyRef = inject(DestroyRef);
  private _descriptionNA = false;
  private readonly postalCodeSearch = new Subject<string | null>();
  formTitle = '';
  monthly_day_ranges = MONTHLY_DAY_RANGES;
  biweekly_day_ranges = BIWEEKLY_DAY_RANGES;
  list_weeks = MONTH_WEEKS;
  week_days = WEEK_DAYS;
  apiUrl: string = environment.AWS_REST_WSPREFIX;
  @Input() settlement: Settlement | null = null;
  @Input() profile: any;
  @Input() permissions: any;
  // @Input() availZones: any[] = [];
  @Output() updatedSettlement: EventEmitter<any> = new EventEmitter<any>();
  @Output() onDismiss: EventEmitter<any> = new EventEmitter<any>();

  settlementForm: FormGroup = new FormGroup({});

  loading: boolean = false;
  searchingZip: boolean = false;
  imageURL: any = '';
  link: string = '';
  identificacion: string = '';

  color: any = "";

  imageError: string = "";
  isImageSaved: boolean = false;
  cardImageBase64: string = "";
  previewImagePath: string = "";
  
  availZones: any[] = [];

  cpResults: any[] = [];
  regionControl = new FormControl();

  periodicities: Periodicity[] = [];
  periodicitiesCatalog: Periodicity[] = [];
  periodicityTypesCatalog: PeriodicityType[] = [];
  periodicityTypes: PeriodicityType[] = [];
  discountTypes: DiscountType[] = [...DEFAULT_DISCOUNT_TYPES];
  readonly formatContractDate = formatContractDate;
  isComercial!: boolean;
  isDetailMode = false;

  weekDays: any[] = [
    { id: 1, name: 'Lunes' },
    { id: 2, name: 'Martes' },
    { id: 3, name: 'Miércoles' },
    { id: 4, name: 'Jueves' },
    { id: 5, name: 'Viernes' },
    { id: 6, name: 'Sábado' },
    { id: 7, name: 'Domingo' },
  ]

  canEditPublished: boolean = false;

  constructor(
    private store: SettlementsService,
    private route: Router,
    private toastr: ToastrService,
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private breadcrumbService: BreadcrumbService,
    public catalogs: CatalogsService,
    public icons: IconsService,
    private authService: AuthService
  ) {}

  private loadSettlement(): void {
    this.activatedRoute.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: any) => {
      //console.log('params ==> ', params);
      this.loading = true;
      this.isDetailMode = params['id'] != undefined;
      if (this.isDetailMode) {
        this.store.getSettlement(params['id']).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (data: any) => {
            this.patchSettlement(data);
            this.loading = false;
          },
          error: (error: any) => {
            this.loading = false;
            //console.log(error);
          }
        });
      } else {
        this.loading = false;
      }
    });
  }

  private patchSettlement(data: Settlement): void {
    this.settlement = data;
    this.postalCodeSearch.next(null);
    this.settlementForm.patchValue(data, { emitEvent: false });
    this.descriptionNA = !data.description?.trim();
    this.cpResults = [];
    this.isComercial = Number(data.tipo) === 1;
    this.formTitle = this.isComercial ? 'comercio' : 'condominio';
    this.updateFormAccessibility();
    this.syncPeriodicityFromType(true);
    this.color = data.code;
  }

  get descriptionNA() {
    return this._descriptionNA;
  }

  set descriptionNA(value: boolean) {
    this._descriptionNA = value;
    const control = this.settlementForm.get('description');
    if (value) control?.removeValidators(Validators.required);
    else control?.addValidators(Validators.required);
    control?.updateValueAndValidity({ emitEvent: false });
  }

  get getPeriodicityConfigGroup() {
    return this.settlementForm.get('periodicity_configuration_attributes') as FormGroup;
  }

  get pcgc() {
    return (this.settlementForm.get('periodicity_configuration_attributes') as FormGroup).controls;
  }

  get periodicityTypesControl() {
    return this.settlementForm.get('periodicity_configuration_attributes')?.get('periodicity_type_id');
  }

  private getSelectedPeriodicityTypeId(): number | null {
    const rawValue = this.periodicityTypesControl?.value;
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      return null;
    }
    return Number(rawValue);
  }

  private loadPeriodicityTypesByCategory(categoryId: number | null): void {
    this.periodicityTypes = this.periodicityTypesCatalog.filter(type =>
      Number(type.periodicity_category?.id) === categoryId);
    if (this.periodicityTypes.length === 1 && !this.getSelectedPeriodicityTypeId()) {
      this.periodicityTypesControl?.setValue(this.periodicityTypes[0].id, { emitEvent: false });
    }
    this.setValidatorsByPeriodicityType();
  }

  private updatePeriodicityControlState(): void {
    if (this.isFormLocked) {
      return;
    }
    const periodicityControl = this.settlementForm.get('periodicity');
    if (!periodicityControl) {
      return;
    }
    if (this.isComercial) {
      periodicityControl.enable({ emitEvent: false });
    } else {
      periodicityControl.disable({ emitEvent: false });
    }
  }

  private syncPeriodicityFromType(isInit = false): void {
    const periodicityControl = this.settlementForm.get('periodicity');
    if (!periodicityControl) {
      return;
    }
    const typeId = this.getSelectedPeriodicityTypeId();
    const typeCategoryId = this.getPeriodicityCategoryId(typeId);
    this.periodicities = this.isComercial
      ? [...this.periodicitiesCatalog]
      : this.periodicitiesCatalog.filter(period => period.name === 'MONTHLY');
    const categoryId = this.isComercial ? typeCategoryId : this.periodicities[0]?.id;
    if (!this.isComercial && categoryId && typeCategoryId && typeCategoryId !== categoryId) {
      this.clearPeriodicityControls();
    }
    if (categoryId && periodicityControl.value !== categoryId) {
      periodicityControl.setValue(categoryId, { emitEvent: false });
    }
    const periodicityRaw = periodicityControl.value;
    const periodicityId = periodicityRaw === '' || periodicityRaw === null || periodicityRaw === undefined
      ? null
      : Number(periodicityRaw);
    if (periodicityId) {
      this.setPeriodicityUI(periodicityId, isInit);
    }
    this.updatePeriodicityControlState();
  }

  ngOnInit(): void {
    const formType = this.activatedRoute.snapshot.params['type'];
    this.isComercial = formType ? formType === 'comercio' : this.route.url.includes('/comercials/');
    this.formTitle = this.isComercial ? 'comercio' : 'condominio';
    this.isDetailMode = this.activatedRoute.snapshot.params['id'] != undefined;
    this.buildSettlementForm();
    this.initializePostalCodeSearch();
    this.loadSettlement();
    this.getPeriodicityData();
    this.loadDiscountTypes();
    this.getPeriodicityTypesData();
    this.loadZones();
    this.checkPermissions();
    this.breadcrumbService.set('@condominioName', this.settlementForm.value.name ? this.settlementForm.value.name : 'Editando condominio');
  }

  searchPostalCode(event: Event): void {
    this.postalCodeSearch.next((event.target as HTMLInputElement).value);
  }

  private initializePostalCodeSearch(): void {
    this.postalCodeSearch.pipe(
      switchMap(query => {
        this.cpResults = [];
        this.searchingZip = false;
        if (!query || query.length < 2) {
          return of([]);
        }
        return timer(300).pipe(
          switchMap(() => {
            this.searchingZip = true;
            return this.store.getTenantRegions(query);
          }),
          finalize(() => { this.searchingZip = false; }),
          catchError((error: any) => {
            console.error('Error searching ZIP codes:', error);
            this.toastr.error('Error al buscar códigos postales', 'Error');
            return of([]);
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((results: any) => {
      this.cpResults = results || [];
    });
  }

  getPeriodicityData(): void {
    this.store.getPeriodicityList().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: PeriodicityCategoriesResponse) => {
        this.periodicitiesCatalog = (res.data && res.data.length)
          ? res.data
          : [];
        this.syncPeriodicityFromType(true);
      },
      error: () => { }
    });
  }

  getPeriodicityTypesData(): void {
    this.store.getPeriodicityTypes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: PeriodicityTypesResponse) => {
        this.periodicityTypesCatalog = (res.data && res.data.length)
          ? res.data
          : [];
        this.syncPeriodicityFromType(true);
      },
      error: () => { }
    });
  }

  private getPeriodicityCategoryId(typeId?: number | null): number | null {
    if (!typeId || this.periodicityTypesCatalog.length === 0) {
      return null;
    }
    const match = this.periodicityTypesCatalog.find((type) => type.id === typeId);
    return match?.periodicity_category?.id ?? null;
  }

  setPeriodicityUI(periodicityId: number, isInit = false) : void {
    if (!isInit) {
      this.clearPeriodicityControls();
    }
    switch (periodicityId) {
      // Mensual
      case 1:
        this.list_weeks = MONTH_WEEKS;
        break;
      // Quincenal
      case 2:
        break;
      // Catorcenal
      case 3:
        this.list_weeks = FOURTEEN_WEEKS;
        break;
      // Semanal
      case 4:
        break;
    }

    this.loadPeriodicityTypesByCategory(periodicityId);
  }

  periodicityChange(event: Event, periodicityInit?: number): void {
    if (!this.isComercial) {
      this.syncPeriodicityFromType(true);
      return;
    }
    const periodicityId = event ? Number((event.target as HTMLSelectElement).value) : periodicityInit;
    const init = event ? false : true;
    this.settlementForm.get('periodicity')?.setValue(periodicityId, { emitEvent: false });
    this.setPeriodicityUI(periodicityId!, init);
  }

  clearPeriodicityControls(): void {
    const group = this.getPeriodicityConfigGroup;
    Object.keys(group.controls).forEach(control => {
      resetControl(group.controls[control], control === 'periodicity_type_id' ? false : true);
    });
  }

  periodicityTypeChange(event: Event): void {
    const group = this.getPeriodicityConfigGroup;
    Object.keys(group.controls).forEach(control => {
      control !== 'periodicity_type_id' && resetControl(group.controls[control]);
    });
    const typeId = Number(group.controls['periodicity_type_id'].value);
    const typeCategoryId = this.getPeriodicityCategoryId(typeId);
    if (typeCategoryId) {
      this.settlementForm.get('periodicity')?.setValue(typeCategoryId);
    }
    this.loadPeriodicityTypesByCategory(typeCategoryId);
    switch (typeCategoryId) {
      case 1:
        this.list_weeks = MONTH_WEEKS;
        break;
      case 3:
        this.list_weeks = FOURTEEN_WEEKS;
        break;
    }
    this.updatePeriodicityControlState();
    this.setValidatorsByPeriodicityType();
  }

  setValidatorsByPeriodicityType(): void {
    const group = this.getPeriodicityConfigGroup;
    const typeId: number = group.controls['periodicity_type_id'].value;
    const controls = PERIODICITY_CONTROLS_BY_TYPE[typeId];
    ['day', 'week_number', 'weekday'].forEach(name => {
      if (controls && !controls.includes(name)) {
        group.controls[name].setValue(null, { emitEvent: false });
      }
      group.controls[name].clearValidators();
      group.controls[name].updateValueAndValidity({ emitEvent: false });
    });
    if (!controls) {
      return;
    }
    setControlsValidators(
      group,
      controls,
      [Validators.required]
    );
    if (typeId === 4) {
      group.controls['day'].addValidators([
        Validators.min(BIWEEKLY_DAY_RANGES.min),
        Validators.max(BIWEEKLY_DAY_RANGES.max),
        Validators.pattern(/^\d+$/)
      ]);
      group.controls['day'].updateValueAndValidity({ emitEvent: false });
    }
  }

  checkPermissions(): void {
    this.authService.getUserPermissions().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.canEditPublished = this.authService.canEditSettlements();
      this.updateFormAccessibility();
    });
  }

  updateFormAccessibility(): void {
    if (this.isFormLocked) {
      this.settlementForm.disable({ emitEvent: false });
    } else if (this.settlementForm.disabled) {
      this.settlementForm.enable({ emitEvent: false });
      this.settlementForm.get('contract_end_date')?.disable({ emitEvent: false });
      this.updatePeriodicityControlState();
    }
  }

  get isFormLocked(): boolean {
    return this.settlement?.id || this.isDetailMode
      ? !this.authService.canEditSettlements()
      : !this.authService.hasPermission('can_create_settlements');
  }
  
  // Computed property to determine if the settlement is published and the user can't edit it
  get isPublishedAndLocked(): boolean {
    return this.settlementForm.get('published')?.value === true && this.isFormLocked;
  }

  checkRouteUrl() {
    this.breadcrumbService.set('@condominioName', this.settlementForm.value.name ? this.settlementForm.value.name : 'Editando condominio');
    return this.route.url == `/condominios/${this.settlement?.id}`;
  }

  buildSettlementForm() {
    this.settlementForm = this.fb.group({
      id: [''],
      name: ['', Validators.required],
      legal_entity_id: [null, Validators.required],
      address: ['', Validators.required],
      outer_number: [''],
      phone: [''],
      email: ['', Validators.email],
      admin_name: [''],
      description: ['', Validators.required],
      slug: [''],
      factor: [''],
      hide_factor: [false, Validators.required],
      admin_price: ['', Validators.required],
      discount_type_id: [DEFAULT_DISCOUNT_TYPE_ID, Validators.required],
      discount: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      show_discount_on_receipt: [false],
      interests: ['', Validators.required],
      invoice_limit: ['', Validators.required],
      reconexion_fee: ['', Validators.required],
      periodicity: ['', Validators.required],
      //cycle_day: ['', Validators.required],
      country: ['', Validators.required],
      city: ['', Validators.required],
      suburb: ['', Validators.required],
      suburb_type: ['', Validators.required],
      municipality: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', Validators.required],
      contrato: [''],
      vigencia: [''],
      contract_term_years: [
        this.isDetailMode ? null : 2,
        [Validators.required, Validators.min(1), Validators.pattern(/^\d+$/)]
      ],
      automatic_renewal: [this.isDetailMode ? null : true],
      contract_end_date: [{ value: '', disabled: true }],
      tipo: [''],
      latitude: [''],
      longitude: [''],
      utility_id: [''],
      zone_id: ['', Validators.required],
      corte: ['', Validators.required],
      merchant_id: ['', Validators.required],
      region_id: [''],
      credit: ['', Validators.required],
      published: [false],
      settlement_fiscal_attributes: this.fb.group({
        id: [''],
        rfc: ['', [RFCValidator(true, true)]],
        razon_social: [''],
        zip_code: ['', [Validators.pattern(/^\d{5}$/)]],
        regimen_fiscal: [''],
        uso_cfdi: ['']
      }),
      periodicity_configuration_attributes: this.fb.group({
        periodicity_type_id: ['', Validators.required],
        week_number: [''],
        weekday: [''],
        day: ['']
      })
    });

    this.activatedRoute.params.subscribe((params: any) => {
      if (params['type'] === 'condominio') {
        this.settlementForm.patchValue({
          tipo: 0
        });
      } else if (params['type'] === 'comercio') {
        this.settlementForm.patchValue({
          tipo: 1
        });
      } else {
        this.settlementForm.patchValue({
          tipo: this.isComercial ? 1 : 0
        });
      }
    });
  }

  selectCP(result: any) {
    this.postalCodeSearch.next(null);
    this.settlementForm.patchValue({
      zip: result.postalCode,
      city: result.city,
      suburb: result.settlement,
      suburb_type: result.settlementType,
      municipality: result.municipality,
      state: result.state,
      region_id: result.id,
      country: result.country
    }, { emitEvent: false });
    this.cpResults = [];
  }

  get sfc () {
    return this.settlementForm.controls;
  }

  get sfac() {
    return (this.settlementForm.get('settlement_fiscal_attributes') as FormGroup).controls;
  }

  get contractExpirationWarning(): boolean {
    return shouldWarnContractExpiration(
      this.sfc['contract_end_date']?.value,
      this.sfc['automatic_renewal']?.value
    );
  }

  get selectedDiscountType(): DiscountType {
    const selectedId = resolveDiscountTypeId(this.sfc['discount_type_id']?.value);
    return this.discountTypes.find(type => type.id === selectedId) || DEFAULT_DISCOUNT_TYPES[0];
  }

  discountTypeOptionLabel(discountType: DiscountType): string {
    return getDiscountTypeOptionLabel(discountType);
  }

  get discountInputLabel(): string {
    return this.selectedDiscountType.name.toUpperCase() === 'PERCENTAGE'
      ? 'Porcentaje del descuento (%)'
      : 'Descuento por litro ($ IVA incluido)';
  }

  get discountSymbol(): string {
    return this.selectedDiscountType.symbol;
  }

  private loadDiscountTypes(): void {
    this.store.getDiscountTypes().subscribe({
      next: (discountTypes) => {
        if (discountTypes.length) {
          this.discountTypes = discountTypes;
        }
      },
      error: () => {
        this.toastr.warning('No se pudo actualizar el catálogo de descuentos. Se usarán las opciones predeterminadas.');
      }
    });
  }

  loadZones() {
    //console.log('Loading zones for utility ID:', this.profile);
    this.store.getUtility(this.profile.utility_id).subscribe({
      next: (data: any) => {
        this.settlementForm.patchValue({
          utility_id: data.id
        });
        this.availZones = data.zones;
      }, error: (error: any) => {
        console.error("ERROR =====> ", error);
      }
    });
  }

  loadImage(event: any): void {
    if (this.isFormLocked || this.loading) return;
    this.imageError = "";

    if (event.target.files && event.target.files[0]) {
      // Size Filter Bytes
      const max_size = 20971520;
      const allowed_types = ['image/png'];
      const max_height = 15200;
      const max_width = 25600;

      if (event.target.files[0].size > max_size) {
        this.imageError = 'Maximum size allowed is ' + max_size / 1000 + 'Mb';
        // return false;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const image = new Image();
        image.src = e.target.result;
        image.onload = (rs: any): void => {
          const img_height = rs.currentTarget['height'];
          const img_width = rs.currentTarget['width'];
          // //console.log(img_height, img_width);
          if (img_height > max_height && img_width > max_width) {
            this.imageError = 'Maximum dimentions allowed ' + max_height + '*' + max_width + 'px';
            // return false;
          } else {
            const imgBase64Path = e.target.result;
            this.cardImageBase64 = imgBase64Path;
            this.isImageSaved = true;
            this.previewImagePath = imgBase64Path;
            //console.log('imgBase64Path => ', imgBase64Path);
            //console.log('previewImagePath => ', this.previewImagePath);
            let payload = {
              id: this.settlement?.id,
              data: this.previewImagePath
            }
            // //console.log('event => ', event.target.files[0]);
            this.store.updateSettlementImage(this.settlement?.id, payload).subscribe((data: any) => {
              //console.log('data => ', data);
              this.toastr.success('Se actualizó la imagen correctamente.', 'Éxito');
              // this.loadUser(this.userId);
            });
          }
        };
      };

      reader.readAsDataURL(event.target.files[0]);
    }
  }

  loadPDF(event: any): void {
    if (this.isFormLocked || this.loading) return;
    const reader = new FileReader();
    const fileToLoad: File | undefined = event.target.files?.[0];

    if (!fileToLoad) {
      return;
    }

    if (fileToLoad.type !== 'application/pdf' && !fileToLoad.name.toLowerCase().endsWith('.pdf')) {
      this.toastr.error('El contrato debe ser un archivo PDF.', 'Error');
      event.target.value = '';
      return;
    }

    reader.onload = () => {
      const base64 = reader.result;
      if (typeof base64 !== 'string') {
        this.toastr.error('No se pudo leer el contrato.', 'Error');
        return;
      }

      this.settlementForm.patchValue({
        contrato: base64
      });

      const settlementId = this.settlement?.id;
      if (!settlementId) {
        return;
      }

      const payload = {
        id: settlementId,
        data: base64
      };

      this.store.updateSettlementContrato(settlementId, payload).subscribe({
        next: () => this.toastr.success('Se actualizó el contrato correctamente.', 'Éxito'),
        error: () => this.toastr.error('No se pudo actualizar el contrato.', 'Error')
      });
    };
    reader.onerror = () => this.toastr.error('No se pudo leer el contrato.', 'Error');
    reader.readAsDataURL(fileToLoad);
  }

  loadIDPDF(event: any): void {
    if (this.isFormLocked || this.loading) return;
    const reader = new FileReader();
    const ctl = this;
    var base64;
    var fileToLoad = event.target.files[0];
    reader.onload = function(fileLoadedEvent) {
      base64 = fileLoadedEvent.target!.result;
      // Print data in console
      //console.log(base64);
      ctl.settlementForm.patchValue({
        identification: base64
      });
      let payload = {
        id: ctl.settlement?.id,
        data: base64
      }
      ctl.store.updateSettlementIdentificacion(ctl.settlement?.id, payload).subscribe((data: any) => {
        //console.log('data => ', data);
        ctl.toastr.success('Se actualizó la imagen correctamente.', 'Éxito');
        // this.loadUser(this.userId);
      });
    };
    // Convert data to base64
    reader.readAsDataURL(fileToLoad);
  }

  linkPDF() {
    let el = this.settlementForm.value.contrato;
    this.link = `${this.apiUrl}/storage/${el.blob.key.substring(0,2)}/${el.blob.key.substring(2,4)}/${el.blob.key}`;
  }

  downloadContract() {
    let contrato: any = this.settlementForm.value.contrato;
    if (typeof contrato === 'string') {
      const link = document.createElement('a');
      link.href = contrato;
      link.download = 'contrato.pdf';
      link.click();
      return;
    }

    if (!contrato?.blob?.key) {
      this.toastr.error('No se pudo descargar el contrato.', 'Error');
      return;
    }

    let imageUrl = `${this.apiUrl}/storage/${contrato.blob.key.substring(0,2)}/${contrato.blob.key.substring(2,4)}/${contrato.blob.key}`;
      this.getBase64ImageFromUrl(imageUrl).then((result: any) => {
      let contratBase64 = "data:application/octet-stream;base64," + result.split(',')[1];

      const link = document.createElement('a');
      link.href = contratBase64;
      link.download = 'contrato.pdf';
      link.click();
    });
  }

  removeContrato() {
    if (this.isFormLocked || this.loading) return;
    this.settlementForm.patchValue({
      contrato: null,
    });
    this.link = '';
  }

  updateColor(e: any): void {
    //console.log(e.target);
    this.settlementForm.patchValue({
      code: e.target.value
    });
  }

  updateSettlement() {
    if (this.isFormLocked || this.loading) return;
    this.loading = true;
    this.settlementForm.patchValue({
      code: this.color
    });
    //console.log(this.settlementForm.value);

    if (this.settlementForm.invalid) {
      this.settlementForm.markAllAsTouched();
      this.toastr.error('No está completa la información.', 'Error');
      this.loading = false;
      return;
    }

    // Check if user is trying to update a published settlement without permission
    if (this.isPublishedAndLocked) {
      this.toastr.error('No tienes permisos para editar un condominio publicado.', 'Error');
      this.loading = false;
      return;
    }
    if (this.settlement?.id) {
      this.updateSettlementData();
    } else {
      this.createSettlementData();
    }
  }

  publishSettlement() {
    if (this.isFormLocked || this.loading) return;
    // Ask for confirmation before publishing
    if (confirm('¿Está seguro que desea publicar este condominio? Una vez publicado, requerirá permisos especiales para editarlo.')) {
      this.loading = true;
      
      // Set published to true
      this.settlementForm.patchValue({
        published: true,
        code: this.color
      });

      if (this.settlementForm.invalid) {
        this.settlementForm.markAllAsTouched();
        this.toastr.error('No está completa la información. Complete todos los campos requeridos antes de publicar.', 'Error');
        this.loading = false;
        return;
      }

      const body = this.buildSettlementPayload();

      if (this.settlement?.id) {
        this.saveSettlementWithFiscal(body, this.settlement.id).subscribe({
          next: (data: any) => {
            this.toastr.success('El condominio ha sido publicado exitosamente.', 'Éxito');
            this.loading = false;
            this.redirectToTable();
          },
          error: (e: any) => {
            this.toastr.error(`Ocurrió un error al publicar el condominio. Por favor intenta de nuevo más tarde. \n ${e}`, 'Error');
            this.loading = false;
          }
        });
      } else {
        this.saveSettlementWithFiscal(body).subscribe({
          next: (data: any) => {
            this.toastr.success('El condominio ha sido creado y publicado exitosamente.', 'Éxito');
            this.loading = false;
            this.redirectToTable();
          },
          error: (e: any) => {
            this.toastr.error(`Ocurrió un error al crear y publicar el condominio. Por favor intenta de nuevo más tarde. \n ${e}`, 'Error');
            this.loading = false;
          }
        });
      }
    }
  }

  buildSettlementPayload() {
    const body = this.settlementForm.getRawValue();
    if (this.descriptionNA) body.description = '';
    // Existing contracts are versioned through the dedicated file endpoint.
    if (this.settlement?.id) delete body.contrato;
    delete body.contract_end_date;
    body.discount_type_id = buildDiscountTypeReference(body.discount_type_id);
    body.legal_entity_id = legalEntityReference(body.legal_entity_id);
    const configuration = body.periodicity_configuration_attributes;
    const applicableControls = PERIODICITY_CONTROLS_BY_TYPE[configuration.periodicity_type_id];
    for (const name of ['week_number', 'weekday', 'day']) {
      if (applicableControls && !applicableControls.includes(name)) {
        configuration[name] = null;
      } else if (['', null, undefined].includes(configuration[name])) {
        delete configuration[name];
      }
    }

    delete body.settlement_fiscal_attributes;

    return body;
  }

  redirectToTable() {
    if (this.isComercial) {
      this.route.navigate(['/comercials']);
    } else {
      this.route.navigate(['/condominios']);
    }
  }

  updateSettlementData() {
    const body = this.buildSettlementPayload();
    this.saveSettlementWithFiscal(body, this.settlement?.id).subscribe({
      next: (data: any) => {
        this.toastr.success('Se ha actualizado el condominio.', 'Éxito');
        this.loading = false;
        this.redirectToTable();
      },
      error: (e: any) => {
        this.toastr.error(`Ocurrió un error por favor intenta de nuevo más tarde. \n ${e}`, 'Error');
        this.loading = false;
      }     
    });
  }

  createSettlementData() {
    const body = this.buildSettlementPayload();
    this.saveSettlementWithFiscal(body).subscribe({
      next: (data: any) => {
        this.toastr.success('Se ha creado el condominio.', 'Éxito');
        this.loading = false;
        this.redirectToTable();
      },
      error: (e: any) => { 
        this.toastr.error(`Ocurrió un error por favor intenta de nuevo más tarde. \n ${e}`, 'Error');
        this.loading = false;
      }
    });
  }

  private saveSettlementWithFiscal(body: any, settlementId?: number): Observable<any> {
    const fiscalAttributes = this.settlementForm.getRawValue()
      .settlement_fiscal_attributes as SettlementFiscalAttributes | undefined;
    const settlementRequest = settlementId
      ? this.store.updateSettlement(settlementId, body)
      : this.store.createSettlement(body);

    return settlementRequest.pipe(
      switchMap(response => {
        if (!this.hasFiscalData(fiscalAttributes)) {
          return of(response);
        }

        const resolvedSettlementId = settlementId ?? this.resolveSettlementId(response);
        if (!resolvedSettlementId) {
          return throwError(() => new Error(
            'No fue posible completar el registro de los datos fiscales. Inténtalo nuevamente.'
          ));
        }

        const fiscalPayload = this.buildFiscalPayload(fiscalAttributes!);
        const fiscalRequest = fiscalAttributes?.id
          ? this.store.updateSettlementFiscal(
              resolvedSettlementId,
              Number(fiscalAttributes.id),
              fiscalPayload
            )
          : this.store.createSettlementFiscal(resolvedSettlementId, fiscalPayload);

        return fiscalRequest.pipe(map(() => response));
      })
    );
  }

  private hasFiscalData(fiscalAttributes?: SettlementFiscalAttributes): boolean {
    if (!fiscalAttributes) {
      return false;
    }

    if (fiscalAttributes.id) {
      return true;
    }

    return [
      fiscalAttributes.rfc,
      fiscalAttributes.razon_social,
      fiscalAttributes.zip_code,
      fiscalAttributes.regimen_fiscal,
      fiscalAttributes.uso_cfdi
    ].some(value => typeof value === 'string' && value.trim() !== '');
  }

  private buildFiscalPayload(fiscalAttributes: SettlementFiscalAttributes): SettlementFiscalPayload {
    return {
      rfc: fiscalAttributes.rfc?.trim() ?? '',
      business_name: fiscalAttributes.razon_social?.trim() ?? '',
      zip_code: fiscalAttributes.zip_code?.trim() ?? '',
      tax_regime: fiscalAttributes.regimen_fiscal?.trim() ?? '',
      cfdi_use: fiscalAttributes.uso_cfdi?.trim() ?? ''
    };
  }

  private resolveSettlementId(response: any): number | undefined {
    const rawId = response?.data?.id
      ?? response?.id
      ?? response?.settlement?.id
      ?? response?.data?.attributes?.id;
    const id = Number(rawId);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }

  async getBase64ImageFromUrl(imageUrl: any) {
    let res = await fetch(imageUrl);
    let blob = await res.blob();

    return new Promise((resolve, reject) => {
      var reader  = new FileReader();
      reader.addEventListener("load", function () {
          resolve(reader.result);
      }, false);
  
      reader.onerror = () => {
        return reject(this);
      };
      reader.readAsDataURL(blob);
    })
  }

  cancel() {
    if (!this.settlement?.id) {
      this.route.navigate([`/condominios`]);
    } else {
      this.onDismiss.emit();
      window.scrollTo(0, 0);
    }
  }
}
